import { useState, useEffect } from 'react';
import { useScroll, useTransform } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import SEO from '../components/SEO';
import GeoCollector from '../components/GeoCollector';
import ContactSalesModal from '../components/ContactSalesModal';
import ChatWidget from '../components/ChatWidget';
import { pricingTiers } from './landing/data';

import { NavSection } from './landing/NavSection';
import { HeroSection } from './landing/HeroSection';
import { FeaturesSection } from './landing/FeaturesSection';
import { HowItWorksSection } from './landing/HowItWorksSection';
import { LocationPortalSection } from './landing/LocationPortalSection';
import { MobileSection } from './landing/MobileSection';
import { TestimonialsSection } from './landing/TestimonialsSection';
import { PricingSection } from './landing/PricingSection';
import { SecuritySection } from './landing/SecuritySection';
import { ComingSoonSection } from './landing/ComingSoonSection';
import { FAQSection } from './landing/FAQSection';
import { CTASection, AboutSection, BlogSection, CareersSection, ContactSection } from './landing/BottomSections';
import { FooterSection } from './landing/FooterSection';
import { DemoModal } from './landing/DemoModal';

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [openFaq, setOpenFaq] = useState(null);
  const [contactSalesOpen, setContactSalesOpen] = useState(false);
  const [demoModalOpen, setDemoModalOpen] = useState(false);
  const [dynamicPlans, setDynamicPlans] = useState(null);
  const [activeSection, setActiveSection] = useState('');
  const { user } = useAuth();
  const { scrollYProgress } = useScroll();
  const heroY = useTransform(scrollYProgress, [0, 0.3], [0, -50]);

  useEffect(() => {
    const sectionIds = ['features', 'testimonials', 'pricing', 'faq', 'about', 'blog', 'contact'];
    const handleScroll = () => {
      const scrollY = window.scrollY + 120;
      let current = '';
      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= scrollY) current = id;
      }
      setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL || '';
        const res = await fetch(`${API}/api/plans/public`);
        if (res.ok) {
          const data = await res.json();
          setDynamicPlans(data.map(p => ({
            name: p.name, price: p.monthly_price, annual: p.annual_price,
            description: p.description, features: p.feature_labels,
            cta: p.cta_text || 'Start Free Trial', popular: p.is_popular || false,
            isContactUs: false, tier: p.tier,
          })));
        }
      } catch (e) { /* fallback to static */ }
    };
    fetchPlans();
  }, []);

  const activePricingTiers = dynamicPlans || pricingTiers;

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden w-full max-w-[100vw]" data-testid="landing-page">
      <SEO
        title="Amusement & Skill Games Management Software"
        description="Manage skill games, slot machines, casino games, claw machines & coin pushers. Automate accounting, inventory, invoicing & routes. Start your free 7-day trial."
        canonical="https://slotfleet.com"
        schema={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'Organization',
              name: 'SlotFleet',
              url: 'https://slotfleet.com',
              description: 'The leading skill games and amusement machine management platform.',
              contactPoint: { '@type': 'ContactPoint', contactType: 'sales', url: 'https://slotfleet.com/signup' }
            },
            {
              '@type': 'WebApplication',
              name: 'SlotFleet',
              url: 'https://slotfleet.com',
              applicationCategory: 'BusinessApplication',
              operatingSystem: 'Web',
              offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD', description: 'Free 7-day trial' },
              description: 'Manage skill games, slot machines, claw machines & coin pushers across multiple companies & locations.'
            }
          ]
        }}
      />
      <GeoCollector />

      <NavSection user={user} mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} activeSection={activeSection} />

      <main>
        <HeroSection setDemoModalOpen={setDemoModalOpen} heroY={heroY} />
        <FeaturesSection />
        <HowItWorksSection />
        <LocationPortalSection />
        <MobileSection />
        <TestimonialsSection />
        <PricingSection billingCycle={billingCycle} setBillingCycle={setBillingCycle} activePricingTiers={activePricingTiers} setContactSalesOpen={setContactSalesOpen} />
        <SecuritySection />
        <ComingSoonSection />
        <FAQSection openFaq={openFaq} setOpenFaq={setOpenFaq} />
        <CTASection user={user} />
        <AboutSection />
        <BlogSection />
        <CareersSection />
        <ContactSection />
      </main>

      <FooterSection />
      <DemoModal open={demoModalOpen} onOpenChange={setDemoModalOpen} onContactSales={setContactSalesOpen} />
      <ContactSalesModal open={contactSalesOpen} onOpenChange={setContactSalesOpen} source="landing_page" />
      <ChatWidget />
    </div>
  );
};

export default LandingPage;
